import { prettifySalesCategory } from "api-types/builders/salesCategory";
import type { ClientOptionChangeEvent } from "api-types/optionCatalogs/optionCatalogHistoryResponses";
import { camelCaseToTitleCase } from "common/utils/strings";
import Capsule from "ui/components/Capsule";
import type { CapsuleProps } from "ui/components/Capsule/Capsule";
import IconAdd from "ui/icons/IconAdd";
import IconEditPen from "ui/icons/IconEditPen";
import IconTrash from "ui/icons/IconTrash";
import styles from "./optionHistory.module.scss";

function displayValue(val: string | null): string {
  if (val === null) {
    return "null";
  }
  if (val === "") {
    return '""';
  }
  return val;
}

function ChangeValueCell({ oldValue, newValue }: { oldValue: string | null; newValue: string | null }) {
  const oldDisplay = displayValue(oldValue);
  const newDisplay = displayValue(newValue);
  return (
    <span className={styles.changeCell} title={`${oldDisplay} → ${newDisplay}`}>
      <span className={styles.oldValue}>{oldDisplay}</span>
      <span className={styles.newValue}>{newDisplay}</span>
    </span>
  );
}

export function dateBadgeCell(event: ClientOptionChangeEvent) {
  const { timestamp, changeType, field } = event;
  const d = new Date(timestamp);
  const dateStr = `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")} ${d
    .getHours()
    .toString()
    .padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  let icon = <IconEditPen size={11} />;
  let bgColor: CapsuleProps["backgroundColor"] = "greenLightest";
  let fgColor: CapsuleProps["color"] = "greenDark";
  let titleSuffix = "Catalog";

  if (field === "archived") {
    icon = <IconTrash size={11} />;
    bgColor = "rubyLightX2";
    fgColor = "rubyDark";
    titleSuffix = "Archived";
  } else if (field === "created") {
    icon = <IconAdd size={11} />;
    bgColor = "blueLightest";
    fgColor = "blueDark";
    titleSuffix = "Created";
  } else if (changeType !== "CATALOG") {
    bgColor = "purpleLightest";
    fgColor = "purpleDark";
    titleSuffix = camelCaseToTitleCase(changeType.toLowerCase());
  }

  return (
    <span className={styles.dateCell} title={`${d.toLocaleString()} — ${titleSuffix}`}>
      <Capsule label={dateStr} icon={icon} size="mini" backgroundColor={bgColor} color={fgColor} />
    </span>
  );
}

export function optionCell(event: ClientOptionChangeEvent) {
  if (event.entityType === "OPTION_SET") {
    return <span className={styles.mutedLabel}>(set)</span>;
  }
  return (
    <span className={styles.optionName} title={event.entityName}>
      {event.entityName}
    </span>
  );
}

export function fieldCell(field: string) {
  const label = camelCaseToTitleCase(field);
  return (
    <Capsule
      label={<span className={styles.fieldLabel}>{label}</span>}
      size="mini"
      backgroundColor="stoneLightX3"
      color="stoneDark"
    />
  );
}

export function changeCell(event: ClientOptionChangeEvent) {
  if (event.field === "created") {
    if (event.entityType === "OPTION_SET" && event.newValue?.includes("options:")) {
      return (
        <span className={styles.archiveSummary} title={event.newValue}>
          {event.newValue}
        </span>
      );
    }
    return <span className={styles.mutedItalic}>First version recorded</span>;
  }
  if (event.field === "archived") {
    if (event.newValue !== "true" && event.newValue !== "false") {
      return (
        <span className={styles.archiveSummary} title={event.newValue ?? ""}>
          {event.newValue}
        </span>
      );
    }
    return <span className={styles.mutedItalic}>{event.newValue === "true" ? "Archived" : "Unarchived"}</span>;
  }

  let { oldValue, newValue } = event;
  if (event.field === "salesCategory") {
    oldValue = oldValue ? prettifySalesCategory(oldValue, undefined) ?? oldValue : oldValue;
    newValue = newValue ? prettifySalesCategory(newValue, undefined) ?? newValue : newValue;
  }
  return <ChangeValueCell oldValue={oldValue} newValue={newValue} />;
}

export function userCell(event: ClientOptionChangeEvent) {
  if (event.userName) {
    return event.userName;
  }
  return (
    <span className={styles.mutedItalic} title="No user was recorded for this change">
      System
    </span>
  );
}

export function truncatedCell(value: string | null) {
  if (!value) {
    return <span className={styles.mutedLabel}>—</span>;
  }
  return (
    <span className={styles.truncatedCell} title={value}>
      {value}
    </span>
  );
}
